import React, { useState } from 'react';
import {
  Stack,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { HowToReg as WalkinIcon } from '@mui/icons-material';
import {
  EasyCheckDialog,
  EasyCheckButton,
  EasyCheckTextField,
  EasyCheckDatePicker,
} from '../../common/Controls';
import { useWalkinCollection } from '../../../hooks/useChequeCollections';

const WalkinCollectionDialog = ({ open, cheque, onClose, onSuccess, onError }) => {
  const walkinMutation = useWalkinCollection();

  const [form, setForm] = useState({
    collectionDate: new Date(),
    collectorName: '',
    collectorId: '',
    signatureObtained: false,
    collectionReference: '',
    remarks: '',
  });

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleSave = async () => {
    if (!form.collectorName.trim()) {
      onError('Collector name is required');
      return;
    }
    if (!form.collectorId.trim()) {
      onError('Collector ID is required');
      return;
    }
    if (!form.signatureObtained) {
      onError('Signature acknowledgement is required');
      return;
    }

    const chequeId = cheque.ChequeId || cheque.chequeId;
    try {
      const result = await walkinMutation.mutateAsync({
        chequeId,
        collectionDate: form.collectionDate?.toISOString() || new Date().toISOString(),
        collectorName: form.collectorName.trim(),
        collectorId: form.collectorId.trim(),
        signatureObtained: form.signatureObtained,
        collectionReference: form.collectionReference.trim() || null,
        remarks: form.remarks.trim() || null,
      });
      if (result.success) {
        onSuccess(result.message || 'Walk-in collection recorded successfully');
      } else {
        onError(result.message || 'Failed to record collection');
      }
    } catch (err) {
      onError('Failed to record collection');
    }
  };

  const chequeNumber = cheque?.ChequeNumber || cheque?.chequeNumber || '';
  const beneficiary = cheque?.BeneficiaryName || cheque?.beneficiaryName || '';

  return (
    <EasyCheckDialog
      open={open}
      onClose={onClose}
      title="Walk-in Collection"
      variant="form"
      titleIcon={<WalkinIcon />}
      actions={
        <>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={onClose}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            onClick={handleSave}
            loading={walkinMutation.isPending}
          >
            Mark as Collected
          </EasyCheckButton>
        </>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cheque <strong>{chequeNumber}</strong> — {beneficiary}
      </Typography>

      <Stack spacing={2}>
        <EasyCheckDatePicker
          label="Collection Date"
          value={form.collectionDate}
          onChange={handleChange('collectionDate')}
          slotProps={{ textField: { required: true, fullWidth: true } }}
        />

        <EasyCheckTextField
          label="Collector Name"
          value={form.collectorName}
          onChange={handleChange('collectorName')}
          required
          fullWidth
          placeholder="Full name of person collecting"
        />

        <EasyCheckTextField
          label="Collector ID / Emirates ID"
          value={form.collectorId}
          onChange={handleChange('collectorId')}
          required
          fullWidth
          placeholder="ID number for verification"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={form.signatureObtained}
              onChange={handleCheckboxChange('signatureObtained')}
              color="primary"
            />
          }
          label="Signature obtained from collector"
        />

        <EasyCheckTextField
          label="Remarks"
          value={form.remarks}
          onChange={handleChange('remarks')}
          fullWidth
          multiline
          rows={2}
          placeholder="Any notes (optional)"
        />
      </Stack>
    </EasyCheckDialog>
  );
};

export default WalkinCollectionDialog;
